import React, { useContext, useState } from 'react';
import * as Yup from 'yup';
import { Formik, Form, Field } from 'formik';
import PropTypes from 'prop-types';
import _ from 'lodash';

import ErrorIcon from '../../assets/images/icon_error_messages.gif';
import { schema, checkFieldToDisplay } from './schema';
import { Wrapper, SpinnerBox, HeadingContent, HeadingBlock } from './css';
import CustomField from '../design/shared/form/customField';
import request from '../utils/request';
import { partnerNames, channelOutletMapping } from '../utils/constant';
import { TranslationsContext } from '../../context/translations';
import { sortedObjects } from '../utils/utils';
import { getLocale } from '../utils';

const isRoleSalesRep = roleId => parseInt(roleId, 10) === 2;
const isRoleSalesSupport = roleId => parseInt(roleId, 10) === 5;

const getChannels = channels =>
  channels &&
  channels.length > 0 &&
  channels.map(({ id, description }) => ({ id, label: description }));
const getArrayFirstId = values => values && values.length > 0 && values[0].id;
const getArrayIds = values =>
  values && values.length > 0 && values.map(item => item.id);

const getInitialValues = user => {
  const {
    id,
    first_name: firstName,
    last_name: lastName,
    email,
    roleId,
    provinces,
    channels,
    dealership,
    xid,
    outlet,
    outlet_code: outletCode,
    contact_number: contactNumber,
    partner_id: partnerId,
    sales_manager: salesManager,
    sales_manager_email: salesManagerEmail,
    channel_org_code: channelOrgCode,
    sales_rep_id: salesRepId
    // adt_rep_id: adtRepId,
  } = user;

  return {
    id,
    roleId,
    firstName,
    lastName,
    email,
    partnerId,
    contactNumber: contactNumber || '',
    outlet: outlet || '',
    outletCode: outletCode || '',
    salesManager: salesManager || '',
    salesManagerEmail: salesManagerEmail || '',
    channelOrgCode: channelOrgCode || '',
    salesRepId: salesRepId || '',
    // adtRepId: adtRepId || '',
    province: isRoleSalesSupport(roleId)
      ? getArrayIds(provinces)
      : getArrayFirstId(provinces),
    channel: isRoleSalesSupport(roleId)
      ? getArrayIds(channels)
      : getChannels(channels),
    dealership: dealership ? [dealership] : [],
    xid: xid || ''
  };
};

const UserForm = ({
  user,
  roles,
  channels,
  dealerships,
  provinces,
  submissionHandling,
  backUrl
}) => {
  const [loading, setLoading] = useState(false);
  const [isError, setError] = useState(null);
  const [initialValues, setInitialValues] = useState(getInitialValues(user));
  const isRoleFieldDisabled = false;
  const displayLabel = text => `${text}*`;
  const {
    USERS: {
      FORM: { LABEL, MESSAGE, BUTTON }
    }
  } = useContext(TranslationsContext);
  const getProvinceDefaultValue = (roleId, value) => {
    if (isRoleSalesSupport(roleId)) {
      return Array.isArray(value) ? value : [parseInt(value, 10)];
    }
    return getArrayFirstId(value);
  };
  const getChannelDefaultValue = (roleId, value) => {
    if (isRoleSalesSupport(roleId)) {
      if (value.length > 0) {
        return typeof value[0] === 'object' ? getArrayIds(value) : value;
      }
      return undefined;
    }
    return getArrayFirstId(value);
  };

  const formFieldList = [
    {
      label: displayLabel(LABEL.ROLE),
      type: 'select',
      name: 'roleId',
      id: 'role-id',
      defaultVal: initialValues.roleId,
      list: roles,
      allowOptionNull: false,
      val: 'description'
    },
    {
      label: displayLabel(LABEL.FIRST_NAME),
      type: 'text',
      name: 'firstName',
      placeholder: LABEL.FIRST_NAME
    },
    {
      label: displayLabel(LABEL.LAST_NAME),
      type: 'text',
      name: 'lastName',
      placeholder: LABEL.LAST_NAME
    },
    {
      label: displayLabel(LABEL.EMAIL),
      type: 'email',
      name: 'email',
      placeholder: LABEL.EMAIL,
      readOnly: true
    },
    {
      label: displayLabel(LABEL.PROVINCE),
      type: isRoleSalesSupport(initialValues.roleId)
        ? 'multiCheckbox'
        : 'select',
      name: 'province',
      defaultVal: getProvinceDefaultValue(
        initialValues.roleId,
        initialValues.province
      ),
      list: provinces,
      val: 'name'
    },
    {
      label: displayLabel(LABEL.CHANNEL),
      type: isRoleSalesSupport(initialValues.roleId)
        ? 'multiCheckbox'
        : 'typeahead',
      name: 'channel',
      id: 'channel',
      defaultVal: getChannelDefaultValue(
        initialValues.roleId,
        initialValues.channel
      ),
      list: sortedObjects(channels, 'description'),
      val: 'description'
    },
    {
      label: displayLabel(LABEL.CONTACT_NUMBER),
      type: 'text',
      name: 'contactNumber',
      placeholder: LABEL.CONTACT_NUMBER
    },
    // {
    //   label: displayLabel(LABEL.ADT_REP_ID),
    //   type: 'text',
    //   name: 'adtRepId',
    //   placeholder: LABEL.ADT_REP_ID
    // },
    {
      label: displayLabel(LABEL.OUTLET),
      type: 'text',
      name: 'outlet',
      placeholder: LABEL.OUTLET
    },
    {
      label: displayLabel(LABEL.OUTLET_CODE),
      type: 'text',
      name: 'outletCode',
      placeholder: LABEL.OUTLET_CODE
    },
    {
      label: displayLabel(LABEL.SALE_REP_ID),
      type: 'text',
      name: 'salesRepId',
      placeholder: LABEL.SALE_REP_ID
    },
    {
      label: displayLabel(LABEL.PARTNER),
      type: 'select',
      name: 'partnerId',
      id: 'partner-id',
      defaultVal: initialValues.partnerId,
      list: partnerNames,
      val: 'text'
    },
    {
      label: displayLabel(LABEL.DEALERSHIP),
      type: 'typeahead',
      name: 'dealership',
      id: 'dealership-id',
      defaultVal:
        initialValues.dealership &&
        initialValues.dealership.length &&
        initialValues.dealership[0].id,
      list: dealerships,
      val: 'description'
    },
    {
      label: displayLabel(LABEL.CHANNEL_ORGANIZATION_CODE),
      type: 'text',
      name: 'channelOrgCode',
      placeholder: LABEL.CHANNEL_ORGANIZATION_CODE
    },
    {
      label: displayLabel(LABEL.XID),
      type: 'text',
      name: 'xid',
      placeholder: LABEL.XID
    },
    {
      label: displayLabel(LABEL.SALE_MANAGER),
      type: 'text',
      name: 'salesManager',
      placeholder: LABEL.SALE_MANAGER
    },
    {
      label: displayLabel(LABEL.SALE_MANAGER_EMAIL),
      type: 'text',
      name: 'salesManagerEmail',
      placeholder: LABEL.SALE_MANAGER_EMAIL
    }
  ];

  const handleSubmit = values => {
    setInitialValues(values);
    setError(null);
    setLoading(true);
    request
      .post(`/users/${values.id}/update`, values)
      .then(({ data }) => data)
      .then(({ status }) => {
        setLoading(false);
        submissionHandling(status, backUrl);
      })
      .catch(err => {
        const {
          data: { error }
        } = err && err.response;
        setLoading(false);
        setError(error);
      });
  };

  const getChannel = outletValue => {
    let result = '';
    Object.keys(channelOutletMapping).forEach(item => {
      if (_.get(channelOutletMapping, item, []).includes(outletValue))
        result = channels.filter(ch => ch.name === item);
    });
    return (
      result &&
      result.map(({ id: cId, description }) => {
        return { id: cId, label: description };
      })
    );
  };

  const getUpdatedField = (field, selectedRoleId) => {
    const updatedField = { ...field };

    if (field.name === 'salesManager') {
      if (isRoleSalesRep(selectedRoleId)) {
        updatedField.label =
          getLocale() === 'EN'
            ? displayLabel(`Sales ${LABEL.SALE_MANAGER}`)
            : displayLabel(LABEL.SALE_MANAGER);
      } else {
        updatedField.label = displayLabel(LABEL.SALE_MANAGER);
      }
    }
    if (field.name === 'salesManagerEmail') {
      if (isRoleSalesRep(selectedRoleId)) {
        updatedField.label =
          getLocale() === 'EN'
            ? displayLabel(`Sales ${LABEL.SALE_MANAGER_EMAIL}`)
            : displayLabel(LABEL.SALE_MANAGER_EMAIL);
      } else {
        updatedField.label = displayLabel(LABEL.SALE_MANAGER_EMAIL);
      }
    }

    if (field.name === 'province') {
      updatedField.type = isRoleSalesSupport(selectedRoleId)
        ? 'multiCheckbox'
        : 'select';
      updatedField.defaultVal = getProvinceDefaultValue(
        selectedRoleId,
        initialValues.province
      );
    }
    if (field.name === 'channel') {
      updatedField.type = isRoleSalesSupport(selectedRoleId)
        ? 'multiCheckbox'
        : 'typeahead';
      updatedField.defaultVal = getChannelDefaultValue(
        selectedRoleId,
        initialValues.channel
      );
    }

    return updatedField;
  };

  return (
    <Wrapper>
      {loading ? (
        <>
          <HeadingBlock>
            <HeadingContent>{MESSAGE.UPDATING_PROFILE}</HeadingContent>
          </HeadingBlock>
          <SpinnerBox>
            <svg viewBox="0 0 100 100" width="0" height="0">
              <circle
                stroke="#66cc00"
                strokeWidth="3"
                fill="none"
                strokeLinecap="round"
                strokeDasharray="89, 200"
                strokeDashoffset="0"
                cx="50"
                cy="50"
                r="20"
              />
            </svg>
          </SpinnerBox>
        </>
      ) : (
        <>
          <HeadingContent>
            <h1>{MESSAGE.FILL_DETAILS}</h1>
          </HeadingContent>
          {isError && (
            <div id="error-message-content" style={{ display: 'block' }}>
              <div id="error-message-icon">
                <img src={ErrorIcon} alt="error_icon" />
              </div>
              <div id="error-message-title">
                <span className="frg-body-font-23px">
                  {MESSAGE.SERVER_ERROR}
                </span>
              </div>
              <div className="clear" />
              <div id="error-message-message">
                <span className="frg-body-font-14px">
                  {isError}
                  <br />
                </span>
              </div>
            </div>
          )}
          <div id="main-content-form">
            <Formik
              initialValues={initialValues}
              validationSchema={Yup.lazy(schema)}
              onSubmit={handleSubmit}
            >
              {({
                errors,
                touched,
                values,
                setFieldValue,
                setFieldTouched
              }) => {
                return (
                  <Form>
                    <Field name="id" type="hidden" />
                    {isRoleFieldDisabled && (
                      <input
                        name="roleId"
                        type="hidden"
                        value={initialValues.roleId}
                      />
                    )}
                    {formFieldList.map((field, index) => {
                      const key = `formField${index}`;
                      const updatedField = getUpdatedField(
                        field,
                        +values.roleId
                      );

                      return (
                        checkFieldToDisplay(
                          values.roleId,
                          field.name,
                          values.channel
                        ) && (
                          <CustomField
                            key={key}
                            field={updatedField}
                            errors={errors}
                            touched={touched}
                            errorIcon={ErrorIcon}
                            setFieldValue={data => {
                              let value;
                              if (
                                ['multicheckbox', 'typehead'].includes(
                                  field.type
                                )
                              ) {
                                value = data.length > 0 ? data[0].id : '';
                                if (
                                  ['channel', 'province'].includes(field.name)
                                )
                                  value = data;
                              } else value = data;

                              setFieldValue(field.name, value, true);
                              if (field.name === 'outlet' && getChannel(value))
                                setFieldValue('channel', getChannel(value));
                            }}
                            setFieldTouched={() =>
                              setFieldTouched(field.name, true)
                            }
                          />
                        )
                      );
                    })}
                    <div id="submit_button" style={{ marginTop: 20 }}>
                      {!loading && (
                        <span>
                          <button
                            type="submit"
                            className="frg-button button-wide color-green"
                          >
                            {BUTTON.SUBMIT}
                          </button>
                        </span>
                      )}
                    </div>
                  </Form>
                );
              }}
            </Formik>
          </div>
        </>
      )}
    </Wrapper>
  );
};

UserForm.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.number,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    email: PropTypes.string,
    provinces: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string
      })
    ),
    status: PropTypes.string,
    contact_number: PropTypes.string,
    outlet: PropTypes.string,
    outlet_code: PropTypes.string,
    dealership: PropTypes.object,
    sales_rep_id: PropTypes.string,
    partner_id: PropTypes.number,
    xid: PropTypes.string,
    adt_rep_id: PropTypes.string,
    channel_org_code: PropTypes.string,
    channels: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        description: PropTypes.string
      })
    ),
    roleId: PropTypes.number,
    role: PropTypes.shape({
      description: PropTypes.string,
      id: PropTypes.number,
      name: PropTypes.string
    }),
    sales_manager: PropTypes.string,
    sales_manager_email: PropTypes.string
  }).isRequired,
  roles: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string,
      id: PropTypes.number,
      name: PropTypes.string
    })
  ).isRequired,
  channels: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string,
      id: PropTypes.number,
      name: PropTypes.string
    })
  ).isRequired,
  dealerships: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string,
      id: PropTypes.number,
      name: PropTypes.string
    })
  ).isRequired,
  provinces: PropTypes.arrayOf(PropTypes.any).isRequired,
  submissionHandling: PropTypes.func.isRequired,
  backUrl: PropTypes.string.isRequired
};

export default UserForm;
